"use client";

import { useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay, Thumbs } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/thumbs";
import "./hero-slider.css";
import { normalizePhotoUrl } from "@/lib/helpers";
import type { GroupsSlider } from "@/lib/groups/sliders";

interface HeroSliderProps {
  sliders: GroupsSlider[];
}

/**
 * Hero slider — `swiper/react` (modular). The main slider cross-fades between
 * slides (robust + bug-free with the curved mask, unlike the old 3D cube whose
 * shadow showed through the curve), and the thumb strip below is linked via the
 * Thumbs module. Each slide's photo is a `next/image` (cover) sitting under the
 * template's dark gradient. Per-slide text animations are pure CSS
 * (`.slideinup`, driven by Swiper's `.swiper-slide-active`).
 */
export default function HeroSlider({ sliders }: HeroSliderProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  if (!sliders.length) return null;

  return (
    <div className="th-hero-wrapper hero-1">
      <div className="heroSlide1-area">
        <Swiper
          className="heroSlide1"
          modules={[EffectFade, Autoplay, Thumbs]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          loop
          speed={1200}
          slidesPerView={1}
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
        >
          {sliders.map((slide, i) => {
            const bg = normalizePhotoUrl(slide.photoURL);
            const btnStyle = i % 2 === 1 ? "style3" : "style1";
            // Hashes are smooth-scrolled by NavigatorScript; internal paths and
            // absolute URLs flow as plain navigations. Fall back to the in-page
            // contact section when the CMS supplied no link.
            const ctaHref = slide.packageId?.trim() || "#contact-sec";

            return (
              <SwiperSlide key={slide.id}>
                <div className="hero-inner">
                  <div className="th-hero-bg background-image">
                    {bg ? (
                      <Image
                        src={bg}
                        alt={slide.title || "Hero slide"}
                        fill
                        sizes="100vw"
                        priority={i === 0}
                        style={{ objectFit: "cover" }}
                      />
                    ) : null}
                  </div>
                  {/* Darkening overlay — a sibling of the photo and the content
                      so the three layers stack cleanly within .hero-inner. */}
                  <span className="th-hero-overlay" aria-hidden="true" />
                  <div className="container">
                    <div className="hero-style1">
                      {slide.subTitle ? (
                        <span
                          className="hero-subtitle slideinup"
                          style={{ animationDelay: "0.3s" }}
                        >
                          {slide.subTitle}
                        </span>
                      ) : null}
                      <div
                        className="hero-review-wrapp slideinup"
                        style={{ animationDelay: "0.4s" }}
                      >
                        <div className="box-review text-warning">
                          <i className="fa-sharp fa-solid fa-star"></i>
                          <i className="fa-sharp fa-solid fa-star"></i>
                          <i className="fa-sharp fa-solid fa-star"></i>
                          <i className="fa-sharp fa-solid fa-star"></i>
                          <i className="fa-sharp fa-solid fa-star"></i>
                        </div>
                        <span className="title">+500k Travellers</span>
                      </div>
                      <h1
                        className="hero-title slideinup"
                        style={{ animationDelay: "0.6s" }}
                      >
                        {slide.title}
                      </h1>
                      <div
                        className="btn-group justify-content-lg-start justify-content-center slideinup"
                        style={{ animationDelay: "0.8s" }}
                      >
                        {/* Plain anchor: in-page hashes are intercepted by
                            NavigatorScript; cross-layout paths trigger a hard
                            reload so the (groups) stylesheet doesn't leak. */}
                        <a
                          href={ctaHref}
                          className={`th-btn2 ${btnStyle} md`}
                          title={slide.title || "More details"}
                        >
                          More Details
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div className="slider-area hero-slider-thumb-wrap">
          <Swiper
            className="hero-grid-thumb"
            modules={[Thumbs]}
            onSwiper={setThumbsSwiper}
            slidesPerView={3}
            spaceBetween={24}
            watchSlidesProgress
            centerInsufficientSlides
          >
            {sliders.map((slide, i) => {
              const thumb = normalizePhotoUrl(slide.photoURL);
              return (
                <SwiperSlide key={slide.id}>
                  <div className="box-img">
                    {thumb ? (
                      <Image
                        src={thumb}
                        alt={slide.title}
                        width={142}
                        height={142}
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                    ) : null}
                    <span className="slider-number">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </SwiperSlide>

              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
